// components/IEP/ChallengeDiagnosis.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  Slider
} from '@mui/material';
import {
  Psychology,
  Assessment,
  Timeline,
  CheckCircle,
  Warning,
  ArrowForward,
  Business,
  Group,
  AttachMoney,
  TrendingUp
} from '@mui/icons-material';

const ChallengeDiagnosis = ({ organization, onClose, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedChallenges, setSelectedChallenges] = useState([]);
  const [diagnosisResult, setDiagnosisResult] = useState(null);

  const steps = ['Industry', 'Challenges', 'Analysis', 'Recommendations'];

  const industries = [
    { id: 'tech', name: 'Technology', icon: '💻' },
    { id: 'healthcare', name: 'Healthcare', icon: '🏥' },
    { id: 'finance', name: 'Financial Services', icon: '💰' },
    { id: 'manufacturing', name: 'Manufacturing', icon: '🏭' },
    { id: 'retail', name: 'Retail', icon: '🛍️' },
    { id: 'education', name: 'Education', icon: '🎓' },
    { id: 'services', name: 'Professional Services', icon: '👔' }
  ];

  const commonChallenges = [
    { id: 'scaling', name: 'Scaling Operations', category: 'operational' },
    { id: 'innovation', name: 'Innovation Stagnation', category: 'strategic' },
    { id: 'digital', name: 'Digital Transformation', category: 'technical' },
    { id: 'talent', name: 'Talent Retention', category: 'cultural' },
    { id: 'costs', name: 'Cost Management', category: 'financial' },
    { id: 'customer', name: 'Customer Retention', category: 'customer' },
    { id: 'process', name: 'Process Inefficiency', category: 'operational' },
    { id: 'risk', name: 'Risk Management', category: 'governance' }
  ];

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      completeDiagnosis();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleChallengeToggle = (challengeId) => {
    setSelectedChallenges(prev => 
      prev.includes(challengeId)
        ? prev.filter(id => id !== challengeId)
        : [...prev, challengeId]
    );
  };

  const completeDiagnosis = () => {
    const result = {
      industry: selectedIndustry,
      challenges: selectedChallenges,
      timestamp: new Date().toISOString(),
      recommendations: [
        { id: 'T7', name: 'Process Intelligence Transformation', priority: 'critical' },
        { id: 'T12', name: 'Talent Amplification Transformation', priority: 'high' },
        { id: 'T4', name: 'Revenue Architecture Transformation', priority: 'medium' }
      ]
    };
    
    setDiagnosisResult(result);
    
    if (onComplete) {
      onComplete(result);
    }
  };

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Psychology sx={{ mr: 2, color: 'primary.main' }} />
          Organizational Challenge Diagnosis
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Your Industry
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Choose the industry that best represents your organization
            </Typography>
            
            <Grid container spacing={2}>
              {industries.map((industry) => (
                <Grid item xs={12} sm={6} md={4} key={industry.id}>
                  <Card
                    elevation={selectedIndustry === industry.id ? 3 : 1}
                    sx={{
                      cursor: 'pointer',
                      border: selectedIndustry === industry.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => setSelectedIndustry(industry.id)}
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ mb: 1 }}>
                        {industry.icon}
                      </Typography>
                      <Typography variant="subtitle1">
                        {industry.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {activeStep === 1 && selectedIndustry && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Identify Your Challenges
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select all challenges your organization is facing (choose 3-5)
            </Typography>
            
            <Grid container spacing={2}>
              {commonChallenges.map((challenge) => (
                <Grid item xs={12} sm={6} key={challenge.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      bgcolor: selectedChallenges.includes(challenge.id) ? 'primary.light' : 'transparent',
                      borderColor: selectedChallenges.includes(challenge.id) ? 'primary.main' : 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                    onClick={() => handleChallengeToggle(challenge.id)}
                  >
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={selectedChallenges.includes(challenge.id)}
                        sx={{ mr: 2 }}
                      />
                      <Box flex={1}>
                        <Typography variant="subtitle1">
                          {challenge.name}
                        </Typography>
                        <Chip 
                          label={challenge.category}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            {selectedChallenges.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Challenges ({selectedChallenges.length}):
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedChallenges.map(id => {
                    const challenge = commonChallenges.find(c => c.id === id);
                    return challenge ? (
                      <Chip 
                        key={id}
                        label={challenge.name}
                        onDelete={() => handleChallengeToggle(id)}
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {activeStep === 2 && selectedChallenges.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              AI Analysis
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Our AI is analyzing your challenges against 14,336 dimensions...
              </Typography>
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Industry Analysis
                  </Typography>
                  <Typography variant="body2">
                    Based on {industries.find(i => i.id === selectedIndustry)?.name || 'your industry'}, 
                    we've identified common patterns and success factors.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Challenge Severity
                  </Typography>
                  <Typography variant="body2">
                    {selectedChallenges.length} challenges detected. 
                    {selectedChallenges.length > 5 ? ' High complexity scenario.' : ' Moderate complexity.'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 3 && diagnosisResult && (
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Diagnosis Complete!
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Based on your {selectedChallenges.length} challenges in {industries.find(i => i.id === selectedIndustry)?.name}, 
                here are your personalized recommendations:
              </Typography>
            </Alert>
            
            <Grid container spacing={2}>
              {diagnosisResult.recommendations.map((rec, index) => (
                <Grid item xs={12} key={rec.id}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle1">
                          {rec.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rec.id} • {rec.priority} priority
                        </Typography>
                      </Box>
                      <Chip 
                        label={`Step ${index + 1}`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={activeStep === 0 ? onClose : handleBack}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Button 
          variant="contained" 
          onClick={handleNext}
          disabled={
            (activeStep === 0 && !selectedIndustry) ||
            (activeStep === 1 && selectedChallenges.length === 0)
          }
          endIcon={<ArrowForward />}
        >
          {activeStep === steps.length - 1 ? 'Complete' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChallengeDiagnosis;